/**
 * The mic's timing rules, kept out of the component so the Node runner can
 * pin them (`__tests__/hold-decision.test.ts`).
 *
 * Source: A2's `coach.tsx:229-276` (the gesture) and `:490, :504` (the guard).
 */

/** A press held this long starts recording. Shorter, and it was a tap. */
export const HOLD_MS = 220;

/** A recording released sooner than this after pointerdown is too short to send. */
export const MIN_RECORDING_MS = 700;

/** Sliding the finger further left than this arms cancel. */
export const CANCEL_SLIDE_PX = 72;

/**
 * How long after a gesture ends a click is taken to be that gesture's own.
 * The browser synthesises a click for the touch that just ended, and it
 * lands where the finger was — exactly where "Parar" appears once recording
 * or listening starts. 450 ms is A2's measured value.
 */
export const GHOST_CLICK_MS = 450;

/** True for a click the gesture that finished at `guardAt` produced itself. */
export function isGhostClick(
  guardAt: number,
  now: number = Date.now()
): boolean {
  return now - guardAt < GHOST_CLICK_MS;
}

/** Whether the finger, `dx` px from where it went down, has armed cancel. */
export function isCancelArmed(dx: number): boolean {
  return dx < -CANCEL_SLIDE_PX;
}

export interface HoldEnd {
  /** Slid away, or the browser took the pointer: throw the recording out. */
  cancelled: boolean;
  /** Released under `MIN_RECORDING_MS`: say "Segure para gravar o áudio". */
  tooShort: boolean;
}

/**
 * How a recording ended. A cancel wins over too-short: a gesture the user
 * threw away is not reported as one they did not hold long enough.
 */
export function holdEnd({
  heldMs,
  cancelArmed,
  interrupted,
}: {
  heldMs: number;
  cancelArmed: boolean;
  interrupted: boolean;
}): HoldEnd {
  const cancelled = interrupted || cancelArmed;
  return { cancelled, tooShort: !cancelled && heldMs < MIN_RECORDING_MS };
}
